import { Drawer } from '@mui/material';
import UPSnackBar from 'components/app-container/snackbar';
import ErrorBar from 'components/error-bar/ErrorBar';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import { getCostCentersDropdownItemsAction } from 'store/actions/company-cost-centers-actions';
import { getCostDeclarationSelectItemsForCompanyAction } from 'store/actions/cost-declaration-actions';
import { contractProcessingBulkConfirmErrorsSelector } from 'store/selectors/contract-processing-selectors';
import theme from 'theme';
import {
  CONTRACTS__CONFIRM_WORKED_HOURS,
  CONTRACTS__COULD_NOT_LOAD_DETAILS,
  CONTRACTS__NO_ACCESS_TO_VIEW,
  CONTRACTS__SAVE_SUCCESS,
  CONTRACT_PROCESSING__INVALID_NO_SHOW_REASON,
  NO_SHOW__REASON_NOT_AVAILABLE,
} from 'translations/constants';
import { ReactComponent as CloseIcon } from '../../assets/icons/Close.svg';
import AuditSection from './components/AuditSection';
import GeneralSection from './components/GeneralSection';
import PanelFooter from './components/PanelFooter';
import WorkingTimesSection from './components/WorkingTimesSection';
import { DETAIL_PANEL_WIDTH } from './constants';
import { useContractDetailPanel } from './hooks/useContractDetailPanel';
import { PanelCloseButton, PanelContentContainer, PanelHeader, PanelHeaderTitle, PanelScrollArea } from './styles';
import { ContractDetailPanelProps } from './types';

const ContractDetailPanel = (props: ContractDetailPanelProps) => {
  const { contractDateId, serviceDateId, companyId, onClose, onSaveSuccess, mode = 'save' } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isSaving, setIsSaving] = useState(false);

  const bulkConfirmErrors = useAppSelector(contractProcessingBulkConfirmErrorsSelector);
  const bulkConfirmError = contractDateId != null ? bulkConfirmErrors[contractDateId] : undefined;

  useEffect(() => {
    dispatch(getCostDeclarationSelectItemsForCompanyAction(companyId));
  }, [companyId, dispatch]);

  const {
    data,
    fetchError,
    clearFetchError,
    saveError,
    clearSaveError,
    successMessage,
    clearSuccessMessage,
    form,
    save,
    saveAndConfirm,
  } = useContractDetailPanel(contractDateId, companyId, serviceDateId);

  const isOpen = contractDateId !== null && data !== null && !fetchError;
  const isReadOnly = mode === 'readOnly' || data?.isEditable === false;
  const isNoShowReasonUnavailable =
    saveError === CONTRACT_PROCESSING__INVALID_NO_SHOW_REASON || bulkConfirmError === NO_SHOW__REASON_NOT_AVAILABLE;

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      if (mode === 'confirm') {
        await saveAndConfirm();
      } else {
        await save();
      }
      onSaveSuccess();
    } catch {
      // Error already handled by hook (saveError) or Redux (bulkConfirmErrors)
    } finally {
      setIsSaving(false);
    }
  }, [mode, save, saveAndConfirm, onSaveSuccess]);

  const handleClose = useCallback(() => {
    form.reset();
    dispatch(getCostCentersDropdownItemsAction({ companyId }));
    onClose();
  }, [form, onClose, dispatch, companyId]);

  return (
    <>
      {/* Fetch error snackbars — shown even when panel is closed */}
      <UPSnackBar
        open={fetchError === 'CONTRACTS__COULD_NOT_LOAD_DETAILS'}
        onClose={() => {
          clearFetchError();
          onClose();
        }}
        content={t(CONTRACTS__COULD_NOT_LOAD_DETAILS)}
        type="error"
        vertical="bottom"
        horizontal="right"
        index={0}
      />
      <UPSnackBar
        open={fetchError === 'CONTRACTS__NO_ACCESS_TO_VIEW'}
        onClose={() => {
          clearFetchError();
          onClose();
        }}
        content={t(CONTRACTS__NO_ACCESS_TO_VIEW)}
        type="error"
        vertical="bottom"
        horizontal="right"
        index={0}
      />
      {/* Save error snackbar */}
      <UPSnackBar
        open={!!saveError}
        onClose={clearSaveError}
        content={saveError ? t(saveError) : saveError}
        type="error"
        vertical="bottom"
        horizontal="right"
        index={0}
      />
      {/* Success snackbar */}
      <UPSnackBar
        open={!!successMessage}
        onClose={clearSuccessMessage}
        content={t(CONTRACTS__SAVE_SUCCESS)}
        type="success"
        vertical="bottom"
        horizontal="right"
        index={0}
      />

      <Drawer
        anchor="right"
        open={isOpen}
        variant="temporary"
        onClose={(_, reason) => {
          // Block backdrop click — only close via buttons
          if (reason === 'backdropClick') return;
          handleClose();
        }}
        ModalProps={{
          keepMounted: false,
        }}
        PaperProps={{
          sx: {
            width: DETAIL_PANEL_WIDTH,
            backgroundColor: theme.palette.info.light,
          },
        }}
      >
        {data && (
          <PanelContentContainer>
            {/* Header */}
            <PanelHeader>
              <PanelHeaderTitle>{t(CONTRACTS__CONFIRM_WORKED_HOURS)}</PanelHeaderTitle>
              <PanelCloseButton type="button" onClick={handleClose}>
                <CloseIcon fill={theme.palette.secondary.contrastText} />
              </PanelCloseButton>
            </PanelHeader>

            {/* Scrollable content */}
            <PanelScrollArea>
              {bulkConfirmError && !isReadOnly && <ErrorBar message={t(bulkConfirmError)} />}
              <GeneralSection
                employeeName={`${data.employeeFirstName} ${data.employeeLastName}`}
                statute={data.statute}
                locationName={data.locationName}
                functionName={data.functionName}
                costCenterName={data.costCenterName ?? ''}
              />
              <WorkingTimesSection
                data={data}
                form={form}
                isNoShowReasonUnavailable={isNoShowReasonUnavailable}
                readOnly={isReadOnly}
              />
              <AuditSection
                clockInOnLocationStatus={data.clockInOnLocationStatus}
                clockInLocationAccuracyRadiusMeters={data.clockInLocationAccuracyRadiusMeters}
                clockOutOnLocationStatus={data.clockOutOnLocationStatus}
                clockOutLocationAccuracyRadiusMeters={data.clockOutLocationAccuracyRadiusMeters}
                serverStartTime={data.serverStartTime}
                serverEndTime={data.serverEndTime}
                serviceDateCreatedDate={data.serviceDateCreatedDate}
                serviceDateCreatedUser={data.serviceDateCreatedUser}
                serviceDateLastModifiedDate={data.serviceDateLastModifiedDate}
                serviceDateLastModifiedUser={data.serviceDateLastModifiedUser}
                contractCreatedDate={data.contractCreatedDate}
                contractCreatedUser={data.contractCreatedUser}
                contractLastModifiedDate={data.contractLastModifiedDate}
                contractLastModifiedUser={data.contractLastModifiedUser}
                showConfirmed={isReadOnly}
                confirmedBy={data.confirmedBy ?? null}
                confirmedAt={data.confirmedAt ?? null}
              />
            </PanelScrollArea>

            {/* Sticky footer */}
            <PanelFooter
              onSave={handleSave}
              onClose={handleClose}
              isSaving={isSaving}
              mode={isReadOnly ? 'readOnly' : mode}
            />
          </PanelContentContainer>
        )}
      </Drawer>
    </>
  );
};

export default ContractDetailPanel;
